import { i18n } from 'i18next'
import { format, formatDistanceStrict } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { dateDifference, dateOnly } from '@/utils/date'

import { getDateFnsLocale } from './i18n'

export const formatDate = (date: Date, dateFormat: string, { i18n, timezone }: { i18n: i18n; timezone?: string }) => {
    if (timezone) {
        return formatInTimeZone(date, timezone, dateFormat, {
            locale: getDateFnsLocale(i18n.language),
        })
    }
    return format(date, dateFormat, { locale: getDateFnsLocale(i18n.language) })
}

export const formatDateOnly = (
    date: Date,
    { i18n, timezone, showTimezone }: { i18n: i18n; timezone?: string; showTimezone?: boolean }
) => {
    return formatDate(date, 'P' + (showTimezone ? ' (zzz)' : ''), { i18n, timezone: timezone || 'UTC' })
}

export const formatTime = (
    date: Date,
    { i18n, timezone, format, showTimezone }: { i18n: i18n; timezone?: string; format?: string; showTimezone?: boolean }
) => {
    return formatDate(date, (format || 'p') + (showTimezone ? ' (zzz)' : ''), { i18n, timezone })
}

export const formatDateTime = (
    date: Date,
    { i18n, timezone, showTimezone }: { i18n: i18n; timezone?: string; showTimezone?: boolean }
) => {
    return formatDate(date, 'P p' + (showTimezone ? ' (zzz)' : ''), { i18n, timezone })
}

type FormatDateDistanceUnit = 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year'

export const formatDateDistance = (
    date: Date,
    {
        i18n,
        unit: sourceUnit = 'auto',
        addSuffix,
        isDateOnly,
    }: {
        i18n: i18n
        unit?: 'auto' | FormatDateDistanceUnit
        addSuffix?: boolean
        isDateOnly: boolean
    }
) => {
    const now = isDateOnly ? dateOnly(new Date()) : new Date()

    let unit: FormatDateDistanceUnit

    if (sourceUnit === 'auto') {
        const delta = Math.abs(dateDifference(date, now, 'seconds'))

        if (!isDateOnly) {
            if (delta < 60) {
                unit = 'second'
            } else if (delta < 3600) {
                unit = 'minute'
            } else if (delta < 86400) {
                unit = 'hour'
            } else if (delta < 2592000) {
                unit = 'day'
            } else if (delta < 31536000) {
                unit = 'month'
            } else {
                unit = 'year'
            }
        } else {
            if (delta < 2592000) {
                unit = 'day'
            } else if (delta < 31536000) {
                unit = 'month'
            } else {
                unit = 'year'
            }
        }
    } else {
        unit = sourceUnit as FormatDateDistanceUnit
    }

    if (unit === 'day') {
        const delta = dateDifference(date, now, 'days')

        switch (delta) {
            case 0:
                return i18n.t('misc.today')
            case -1:
                return i18n.t('misc.yesterday')
            case +1:
                return i18n.t('misc.tomorrow')
            default:
                break
        }
    }

    return formatDistanceStrict(date, now, {
        locale: getDateFnsLocale(i18n.language),
        unit,
        addSuffix,
    })
}
